import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { bankingPartnerService } from '../services/bankingPartnerService';

interface PricingTemplatesListProps {
  partnerId: string;
  onCreateTemplate: () => void;
  onEditTemplate: (templateId: string) => void;
}

export function PricingTemplatesList({
  partnerId,
  onCreateTemplate,
  onEditTemplate,
}: PricingTemplatesListProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [deleteTemplateId, setDeleteTemplateId] = useState<string | null>(null);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['pricingTemplates', partnerId],
    queryFn: () => bankingPartnerService.getAllPricingTemplates(partnerId),
    enabled: !!partnerId,
  });

  const setDefaultMutation = useMutation({
    mutationFn: (templateId: string) =>
      bankingPartnerService.setDefaultTemplate(partnerId, templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricingTemplates', partnerId] });
      toast({
        title: 'Success',
        description: 'Default template updated successfully',
      });
    },
    onError: (error) => {
      console.error('Error setting default template:', error);
      toast({
        title: 'Error',
        description: 'Failed to set default template',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (templateId: string) =>
      bankingPartnerService.deletePricingTemplate(partnerId, templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricingTemplates', partnerId] });
      toast({
        title: 'Success',
        description: 'Template deleted successfully',
      });
      setDeleteTemplateId(null);
    },
    onError: (error) => {
      console.error('Error deleting template:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete template',
        variant: 'destructive',
      });
    },
  });

  const getPricingTypeLabel = (type: string) => {
    switch (type) {
      case 'interchangePlus':
        return 'Interchange+';
      case 'surcharge':
        return 'Surcharge';
      case 'tiered':
        return 'Tiered';
      case 'flatRate':
        return 'Flat Rate';
      default:
        return type;
    }
  };

  const getRiskTypeLabel = (type: string) => {
    switch (type) {
      case 'highRisk':
        return 'High Risk';
      case 'lowRisk':
        return 'Low Risk';
      default:
        return type;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Loading templates...
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Pricing Templates</CardTitle>
              <CardDescription>
                Manage merchant pricing configurations for different scenarios
              </CardDescription>
            </div>
            <Button onClick={onCreateTemplate}>
              <Plus className="mr-2 h-4 w-4" />
              New Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">
                No pricing templates created yet
              </p>
              <Button onClick={onCreateTemplate} variant="outline">
                <Plus className="mr-2 h-4 w-4" />
                Create Your First Template
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Pricing Type</TableHead>
                  <TableHead>Risk Level</TableHead>
                  <TableHead>Processing Fee</TableHead>
                  <TableHead>Transaction Fee</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {template.isDefault && (
                          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        )}
                        <span className="font-medium">{template.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {getPricingTypeLabel(template.pricingType)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          template.riskType === 'highRisk' ? 'destructive' : 'default'
                        }
                      >
                        {getRiskTypeLabel(template.riskType)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {template.pricing.processingFees.visaMasterDiscover}%
                    </TableCell>
                    <TableCell>
                      ${template.pricing.transactionFees.visaMasterDiscover.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        {!template.isDefault && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDefaultMutation.mutate(template.id)}
                            disabled={setDefaultMutation.isPending}
                          >
                            <Star className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEditTemplate(template.id)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTemplateId(template.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={!!deleteTemplateId}
        onOpenChange={() => setDeleteTemplateId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this pricing template? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteTemplateId) {
                  deleteMutation.mutate(deleteTemplateId);
                }
              }}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
